<script lang="ts">
  import { liveQuery } from 'dexie';
  import { writable } from 'svelte/store';
  import { db, type UserProfile } from '../lib/db';
  import { approveUser, rejectUser, setUserRole } from '../lib/auth';

  const users = writable<UserProfile[]>([]);
  liveQuery(() => db.profiles.toArray().then((rows) => rows.sort((a, b) => b.createdAt - a.createdAt)))
    .subscribe((v) => users.set(v));

  $: pending = $users.filter((u) => !u.approved);
  $: active  = $users.filter((u) => u.approved);

  function isoDay(ts: number) { return new Date(ts).toISOString().slice(0, 10); }
</script>

<div class="wrap">
  <header class="head">
    <div class="row meta">
      <span class="chip">admin</span>
      <span class="chip">users.{$users.length}</span>
    </div>
    <h2>User management</h2>
    <p class="dim small">Approve signups. Promote or demote admins. Remove access.</p>
  </header>

  <div class="card stack">
    <div class="row head-row">
      <h3>Pending approval</h3>
      <span class="chip {pending.length ? 'signal' : ''}">{pending.length} queue</span>
    </div>
    {#if !pending.length}
      <p class="dim small">nothing waiting.</p>
    {:else}
      <ul>
        {#each pending as u (u.id)}
          <li>
            <div class="u">
              <span class="email mono">{u.email}</span>
              <span class="dim small mono">signed up {isoDay(u.createdAt)}</span>
            </div>
            <div class="row">
              <button class="primary small" on:click={() => approveUser(u.email)}>approve →</button>
              <button class="danger small" on:click={() => rejectUser(u.email)}>reject</button>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  <div class="card stack">
    <div class="row head-row">
      <h3>Active users</h3>
      <span class="chip">{active.length} total</span>
    </div>
    <ul>
      {#each active as u (u.id)}
        <li>
          <div class="u">
            <span class="email mono">{u.email}</span>
            <span class="chip {u.role === 'admin' ? 'gold' : ''}">{u.role}</span>
          </div>
          <div class="row">
            {#if u.role === 'user'}
              <button class="small" on:click={() => setUserRole(u.email, 'admin')}>→ admin</button>
            {:else}
              <button class="small" on:click={() => setUserRole(u.email, 'user')}>→ user</button>
            {/if}
            <button class="ghost small danger" on:click={() => confirm(`Remove ${u.email}?`) && rejectUser(u.email)}>
              remove
            </button>
          </div>
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  .wrap { max-width: 900px; margin: 36px auto; padding: 0 24px; display: grid; gap: 20px; }
  .head { margin-bottom: 4px; }
  .head .meta { margin-bottom: 14px; }
  h2 { font-size: var(--ls-step-4); margin: 0 0 6px; }
  h3 { margin: 0; font-size: var(--ls-step-3); }
  .small { font-size: var(--ls-step-0); }
  .head-row { justify-content: space-between; margin-bottom: 8px; }
  ul { list-style: none; padding: 0; margin: 0; }
  li {
    display: flex; justify-content: space-between; gap: 12px; align-items: center;
    padding: 12px 0; border-bottom: 1px solid var(--ls-border);
  }
  li:last-child { border-bottom: none; }
  .u { display: flex; flex-direction: column; gap: 4px; }
  .email { font-size: 13px; color: var(--ls-text); }
</style>
